import React, { useState, useMemo, useEffect } from "react"
import PropTypes from "prop-types"
import { Table, Select, Typography, Alert, Flex } from "antd"
import { useTranslation } from "react-i18next"
import styles from "./index.module.css"
import { Icons } from "assets/icons"
import { spacings } from "themes/variables"
import { Text } from "ui/Typography"
import ExternalLink from "ui/Icon/DSIcon/ExternalLink"
import Separator from "ui/Separator"
import { useDevice } from "hooks/useDevice"
import { generateBookingLink, generateTripLink } from "../utils"
import { RESOURCE_TYPES } from "../constants"

const PartiallySubstitutable = ({
  upcomingBookings = [],
  assignedExperiences = [],
  warning,
  onValidityChange,
  onSelectionsChange,
  expMessage,
  bookingMessage,
  resourceType = RESOURCE_TYPES.EQUIPMENT,
  translationPrefix = "equipment"
}) => {
  const { t } = useTranslation()
  const { isMobile } = useDevice()
  const [replacements, setReplacements] = useState(() => {
    const init = {}
    for (const b of upcomingBookings) {
      if (b.replaceWith) init[b.booking_id] = b.replaceWith
    }
    return init
  })

  // Separate bookings into two groups
  const bookingsWithSubstitutes = useMemo(() => {
    return upcomingBookings.filter((b) => {
      const substitutes = b.available_substitutes || []
      return substitutes.length > 0
    })
  }, [upcomingBookings])

  const bookingsWithoutSubstitutes = useMemo(() => {
    return upcomingBookings.filter((b) => {
      const substitutes = b.available_substitutes || []
      return substitutes.length === 0
    })
  }, [upcomingBookings])

  const hasBlockingError = useMemo(() => {
    return upcomingBookings.some((b) => b.errorMessage)
  }, [upcomingBookings])

  const hasNoOptions = useMemo(() => {
    return bookingsWithoutSubstitutes.length > 0
  }, [bookingsWithoutSubstitutes])

  const allSatisfied = useMemo(() => {
    if (hasBlockingError) return false
    if (hasNoOptions) return false
    return bookingsWithSubstitutes.every((b) => replacements[b.booking_id])
  }, [bookingsWithSubstitutes, replacements, hasBlockingError, hasNoOptions])

  useEffect(() => {
    onValidityChange?.(allSatisfied)
  }, [allSatisfied, onValidityChange])

  useEffect(() => {
    onSelectionsChange?.(replacements)
  }, [replacements, onSelectionsChange])

  // Get display name for substitute based on resource type
  const getSubstituteDisplayName = (substitute) => {
    if (resourceType === RESOURCE_TYPES.CREW) {
      if (substitute.role) {
        return `${substitute.crew_name} (${substitute.role})`
      }
      return substitute.crew_name || substitute.name
    } else {
      if (substitute.asset_number) {
        return `Asset #${substitute.asset_number} (${substitute.boat_name})`
      } else if (substitute.available_assets) {
        return `${substitute.boat_name} (${t(`${translationPrefix}.modals.available`)}: ${substitute.available_assets})`
      }
      return substitute.boat_name || substitute.name
    }
  }

  // Get substitute ID based on resource type
  const getSubstituteId = (substitute) => {
    if (resourceType === RESOURCE_TYPES.CREW) {
      return substitute.crew_id || substitute.id
    }
    return substitute.boat_asset_id || substitute.boat_id || substitute.id
  }

  const getSubstituteOptions = (record) => {
    const substitutes = record.available_substitutes || []

    return substitutes.map((substitute) => {
      if (typeof substitute === "object") {
        return {
          label: getSubstituteDisplayName(substitute),
          value: getSubstituteId(substitute)
        }
      }

      return {
        label: substitute,
        value: substitute
      }
    })
  }

  const renderMobileBookingCards = (records) => (
    <Flex vertical className={`${styles.mobileBookingsList} px-m`}>
      {records.map((record) => {
        const experienceName = record.experience_name || record.experienceName
        const bookingLink = generateBookingLink(record)
        const recordId = record.booking_id
        const clientName = record.client_name || record.clientName
        const bookingTime = record.start_time && record.end_time ? `${record.start_time} - ${record.end_time}` : record.time || "-"
        const options = getSubstituteOptions(record)

        if (record.errorMessage) {
          return (
            <Alert
              key={record.key}
              type="error"
              showIcon
              message={
                <span>
                  {record.errorMessage}{" "}
                  {record.onRefresh && (
                    <Typography.Link className={styles.refreshLink} onClick={() => record.onRefresh?.()}>
                      Refresh
                    </Typography.Link>
                  )}
                </span>
              }
              className={styles.errorRow}
            />
          )
        }

        return (
          <Flex vertical key={record.key} className={styles.mobileBookingCard} gap={spacings.XXS}>
            <Typography.Link href={bookingLink} target="_blank" rel="noopener noreferrer" className={styles.externalLink}>
              {experienceName}
              <ExternalLink className="ml-xxs" />
            </Typography.Link>

            <Text className={styles.mobileBookingMeta}>
              {record.booked_date}
              <Separator />
              {clientName}
            </Text>

            <Text className={styles.mobileBookingMeta}>
              {bookingTime}
              <Separator />
              {t(`${translationPrefix}.modals.table.id`)}: {record.booking_id}
            </Text>

            <Select
              className={styles.mobileBookingSelect}
              placeholder={t(`${translationPrefix}.modals.select_replacement`)}
              value={replacements[recordId] || null}
              options={options}
              onChange={(val) => setReplacements((prev) => ({ ...prev, [recordId]: val }))}
              disabled={record.errorMessage || !options.length}
            />
          </Flex>
        )
      })}
    </Flex>
  )

  const columns = [
    {
      title: t(`${translationPrefix}.modals.table.experience_name`),
      key: "experienceName",
      render: (_, record) => {
        if (record.__isErrorRow) {
          return {
            children: (
              <Alert
                type="error"
                showIcon
                message={
                  <span>
                    {record.errorMessage}{" "}
                    {record.onRefresh && (
                      <Typography.Link className={styles.refreshLink} onClick={() => record.onRefresh?.()}>
                        Refresh
                      </Typography.Link>
                    )}
                  </span>
                }
                className={styles.errorRow}
              />
            ),
            props: { colSpan: 6 }
          }
        }
        const experienceName = record.experience_name || record.experienceName
        const bookingLink = generateBookingLink(record)

        return (
          <Flex gap={spacings.XS} align="center">
            <Typography.Link href={bookingLink} target="_blank" rel="noopener noreferrer" className={styles.externalLink}>
              {experienceName}
              <ExternalLink className="ml-xxs" />
            </Typography.Link>
          </Flex>
        )
      }
    },
    {
      title: t(`${translationPrefix}.modals.table.date`),
      key: "date",
      render: (_, record) => {
        if (record.__isErrorRow) return { children: null, props: { colSpan: 0 } }
        return record.booked_date
      }
    },
    {
      title: t(`${translationPrefix}.modals.table.time`),
      key: "time",
      render: (_, record) => {
        if (record.__isErrorRow) return { children: null, props: { colSpan: 0 } }
        if (record.start_time && record.end_time) {
          return `${record.start_time} - ${record.end_time}`
        }
        return record.time || "-"
      }
    },
    {
      title: t(`${translationPrefix}.modals.table.id`),
      key: "id",
      render: (_, record) => {
        if (record.__isErrorRow) return { children: null, props: { colSpan: 0 } }
        return record.booking_id
      }
    },
    {
      title: t(`${translationPrefix}.modals.table.client_name`),
      key: "clientName",
      render: (_, record) => {
        if (record.__isErrorRow) return { children: null, props: { colSpan: 0 } }
        return record.client_name || record.clientName
      }
    },
    {
      title: t(`${translationPrefix}.modals.table.replace_with`),
      key: "replaceWith",
      render: (_, record) => {
        if (record.__isErrorRow) return { children: null, props: { colSpan: 0 } }
        const substitutes = record.available_substitutes || []
        const options = substitutes.map((s) => {
          if (typeof s === "object") {
            return {
              label: getSubstituteDisplayName(s),
              value: getSubstituteId(s)
            }
          }
          return { label: s, value: s }
        })
        const recordId = record.booking_id
        return (
          <Select
            className={styles.replaceSelect}
            placeholder={t(`${translationPrefix}.modals.select_replacement`)}
            value={replacements[recordId] || null}
            options={options}
            onChange={(val) => setReplacements((prev) => ({ ...prev, [recordId]: val }))}
            disabled={record.errorMessage || !options.length}
          />
        )
      }
    }
  ]

  const tableDataWithSubstitutes = bookingsWithSubstitutes.map((b) => ({
    ...b,
    key: b.booking_id
  }))

  const tableDataWithoutSubstitutes = bookingsWithoutSubstitutes.map((b) => ({
    ...b,
    key: b.booking_id
  }))

  return (
    <>
      {upcomingBookings.length > 0 && (
        <div>
          <div className={styles.sectionHeader}>
            {t(`${translationPrefix}.modals.assigned_to_bookings`)} ({upcomingBookings.length})
          </div>
          <div className={styles.sectionContent}>
            {tableDataWithSubstitutes.length > 0 && (
              isMobile ? (
                renderMobileBookingCards(tableDataWithSubstitutes)
              ) : (
                <Table
                  className={styles.upcomingBookingsTable}
                  dataSource={tableDataWithSubstitutes}
                  pagination={false}
                  bordered={false}
                  columns={columns}
                />
              )
            )}

            {bookingsWithoutSubstitutes.length > 0 && (bookingMessage || warning) && (
              <Flex align="center" gap={spacings.XS} className={styles.errorAlert}>
                <Icons.RedCrossWithCircle />
                <Text>{bookingMessage || warning}</Text>
              </Flex>
            )}

            {tableDataWithoutSubstitutes.length > 0 && (
              isMobile ? (
                renderMobileBookingCards(tableDataWithoutSubstitutes)
              ) : (
                <Table
                  dataSource={tableDataWithoutSubstitutes}
                  pagination={false}
                  bordered={false}
                  columns={columns}
                  showHeader={tableDataWithSubstitutes?.length === 0}
                  style={{ marginTop: tableDataWithSubstitutes?.length > 0 || warning ? 16 : 0 }}
                />
              )
            )}
          </div>
        </div>
      )}
      {assignedExperiences.length > 0 && (
        <div className={`${styles.mt16}`}>
          <div className={`${styles.sectionHeader} ${styles.assignedExperiencesHeader}`}>
            {t(`${translationPrefix}.modals.assigned_experiences`)} ({assignedExperiences.length})
          </div>
          <div className={styles.sectionContent}>
            {expMessage && (
              <Flex align="center" gap={spacings.XS} className={styles.warningAlert}>
                <Icons.OrangeAlertTriangle />
                <Text>{expMessage}</Text>
              </Flex>
            )}
            <Table
              className={styles.assignedExperiencesTable}
              dataSource={assignedExperiences}
              columns={[
                {
                  title: "",
                  key: "name",
                  render: (_, record) => {
                    const { trip_name, name, trip_id, status } = record || {}
                    const tripName = trip_name || name
                    const tripLink = generateTripLink({ trip_id, status })

                    return (
                      <Flex gap={spacings.XS} align="center">
                        <Typography.Link href={tripLink} target="_blank" rel="noopener noreferrer" className={styles.externalLink}>
                          {tripName}
                          <ExternalLink className="ml-xxs" />
                        </Typography.Link>
                      </Flex>
                    )
                  }
                }
              ]}
              pagination={false}
              showHeader={false}
              rowClassName={() => styles.experienceTableRow}
            />
          </div>
        </div>
      )}
    </>
  )
}

PartiallySubstitutable.propTypes = {
  upcomingBookings: PropTypes.array,
  assignedExperiences: PropTypes.array,
  warning: PropTypes.string,
  onValidityChange: PropTypes.func,
  onSelectionsChange: PropTypes.func,
  expMessage: PropTypes.string,
  bookingMessage: PropTypes.string,
  resourceType: PropTypes.oneOf(Object.values(RESOURCE_TYPES)),
  translationPrefix: PropTypes.string
}

export default PartiallySubstitutable
